import type { AxiosRequestConfig } from 'axios';
import type { ContentFlagContentType } from 'src/types/content-flag';

import axios from 'axios';

import { AUTH } from 'src/lib/firebase';
import { CONFIG } from 'src/config-global';
import { reportAxiosError } from 'src/lib/report-axios-error';

import { displayAxiosError } from 'src/components/snackbar';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: CONFIG.serverUrl });

// Request interceptor to add Client-Type header
axiosInstance.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};
    config.headers['Client-Type'] = 'cms';
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor with retry logic for network errors
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Log authentication errors for debugging
    if (error.response?.status === 401) {
      console.error('Authentication error', error.response);
      // Try to refresh token on 401
      try {
        const auth = AUTH;
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken(true);
          if (token && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            // Retry the original request with new token
            return await axiosInstance(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error('Failed to refresh token on 401:', refreshError);
      }
    }

    // Retry logic for network errors (timeout, network error, etc.)
    if (
      (error.code === 'ECONNABORTED' || // Timeout
        error.code === 'ERR_NETWORK' || // Network error
        error.message === 'Network Error' ||
        !error.response) // No response (connection lost)
    ) {
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

      // Maximum 3 retries
      if (originalRequest._retryCount <= 3) {
        // Exponential backoff: 1s, 2s, 4s
        const delay = 2 ** (originalRequest._retryCount - 1) * 1000;

        // Refresh token before retry
        try {
          const auth = AUTH;
          const user = auth.currentUser;
          if (user) {
            const token = await user.getIdToken(true);
            if (token && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
          }
        } catch (refreshError) {
          console.error('Failed to refresh token before retry:', refreshError);
        }

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delay));

        return axiosInstance(originalRequest);
      }
    }

    reportAxiosError(error);
    return Promise.reject(error);
  }
);

export default axiosInstance;

const includeDraftQuery = (includeDraft: boolean) => (includeDraft ? 'includedraft=true' : '');

export const getEndpoints = {
  place: {
    list: (orgGuid: string) =>
      `/organization/${orgGuid}/places?`,
    base: (placeGuid: string, includeDraft: boolean = true) =>
      `/place/${placeGuid}/base?${includeDraftQuery(includeDraft)}`,
    summary: (placeGuid: string) => `/public/place/${placeGuid}/summary`,
    parentable: (orgGuid: string, includeDraft: boolean = true) =>
      `/organization/${orgGuid}/places/parentable?limit=500&offset=0&${includeDraftQuery(includeDraft)}`,
    citations: (placeGuid: string) => `/place/${placeGuid}/citations`,
    public: (publicId: string) => `/public/${publicId}`,

    images: (placeGuid: string) => `/place/${placeGuid}/images`,
    location: (placeGuid: string) => `/place/byguid/${placeGuid}/location`,
    timeline: (placeGuid: string) => `/place/${placeGuid}/timeline`,
    children: (placeGuid: string, includeDraft: boolean = true) =>
      `/place/${placeGuid}/children?${includeDraftQuery(includeDraft)}`,
    adoptablePlaces: (placeGuid: string) => `/place/${placeGuid}/adoptablePlaces`,
    root: (lat: number, lng: number) => `/public/place/root?lat=${lat}&lon=${lng}`,
    floors: (placeGuid: string) => `/public/place/${placeGuid}/venue/floors`,
    contentTopicCounts: (placeGuid: string) => `/place/${placeGuid}/contentTopicCounts`,
    storyStatusCounts: (placeGuid: string) => `/place/${placeGuid}/stories/statusCounts`,
    reviewCounts: (placeGuid: string) => `/place/${placeGuid}/stories/reviewCounts`,
    externalIds: (placeGuid: string) => `/place/${placeGuid}/externalIds`,
  },
  story: {
    list: (orgGuid: string) => `/organization/${orgGuid}/stories`,
    listByPlace: (placeGuid: string, includeDraft: boolean = true) =>
      `/place/${placeGuid}/stories?${includeDraftQuery(includeDraft)}`,
    listByLandmark: (type: string, guid: string, includeDraft: boolean = true) =>
      `/place/${guid}/stories?${includeDraftQuery(includeDraft)}`,
    public: (publicID: string) => `/public/${publicID}`,
    details: (storyGuid: string) => `/public/story/${storyGuid}/details`,
    citations: (storyGuid: string) => `/story/${storyGuid}/citations`,
  },
  event: {
    listByOrg: (orgGuid: string, includeDraft: boolean = true) =>
      `/organization/${orgGuid}/events?${includeDraftQuery(includeDraft)}`,
    listByPlace: (placeGuid: string, includeDraft: boolean = true) =>
      `/public/place/${placeGuid}/events?locType=place&${includeDraftQuery(includeDraft)}`,
    listByPlaceAdmin: (placeGuid: string) => `/place/${placeGuid}/events?includedraft=true&includeexpired=true`,
    details: (eventGuid: string) => `/public/event/${eventGuid}/details`,
  },
  image: {
    citations: (imageGuid: string) => `/image/${imageGuid}/citations`,
  },
  creator: {
    listForOrg: (orgGuid: string) => `/organization/${orgGuid}/creators`,
  },
  organization: {
    base: (orgGuid: string) => `/organization/${orgGuid}`,
    users: (orgGuid: string) => `/organization/${orgGuid}/users`,
  },
  user: {
    byGuid: (guid: string) => `/user/${guid}`,
    byId: (userId: number) => `/user/${userId}`,
    byAuthId: (userId: string) => `/user/authid/${userId}`, // get token from firebase, pass here for getting profile
  },
  guide: {
    listByOrg: (orgGuid: string, includeDraft: boolean = true) =>
      `/organization/${orgGuid}/guides?${includeDraftQuery(includeDraft)}`,
    details: (guideGuid: string) => `/guide/${guideGuid}/full`,
    listByPlace: (placeGuid: string, includeDraft: boolean = true) =>
      `/public/place/${placeGuid}/guides?locType=place&${includeDraftQuery(includeDraft)}`,
    listByStories: (storyGuid: string) => `/story/${storyGuid}/guides`,
    contentTopicCounts: (guideGuid: string) => `/guide/${guideGuid}/contentTopicCounts`,
  },
  placetypes: {
    list: `/placetypes`,
  },
  constants: {
    guideTimeEstimates: `/constants/guide/timeEstimates`,
  },
  analytics: {
    orgMetrics: (orgGuid: string) => `/analytics/org/${orgGuid}/metrics`,
    topPlaces: (orgGuid: string) => `/analytics/org/${orgGuid}/topplaces`,
    topStories: (orgGuid: string) => `/analytics/org/${orgGuid}/topstories`,
    users: (orgGuid: string) => `/analytics/org/${orgGuid}/users`,
  },
  contentFlag: {
    reasons: `/contentFlag/reasons`,
    byGuid: (guid: string) => `/contentFlag/${guid}`,
    listByStory: (guid: string) => `/story/${guid}/contentFlags`,
    listByEvent: (guid: string) => `/event/${guid}/contentFlags`,
  },
  atlas: {
    analytics: {
      stories: {
        review: {
          reviewStatusCounts: (orgGuid: string) => `/organization/${orgGuid}/atlas/analytics/stories/review/statusCounts`,
          failureReasonCounts: (orgGuid: string) => `/organization/${orgGuid}/atlas/analytics/stories/review/failureReasonCounts`,
          contentFlagCounts: (orgGuid: string) => `/organization/${orgGuid}/atlas/analytics/stories/review/contentFlagCounts`,
          publishedCounts: (orgGuid: string) => `/organization/${orgGuid}/atlas/analytics/stories/review/publishedCounts`,
        },
        sensitiveContent: {
          deletedCounts: (orgGuid: string) => `/organization/${orgGuid}/atlas/analytics/stories/sensitiveContent/deletedCounts`,
          contentFlagStateCounts: (orgGuid: string) => `/organization/${orgGuid}/atlas/analytics/stories/sensitiveContent/contentFlagStateCounts`,
          sensitivityBreakdownCounts: (orgGuid: string) => `/organization/${orgGuid}/atlas/analytics/stories/sensitiveContent/sensitivityBreakdownCounts`,
          mostUsedRequiresReviewContentTags: (orgGuid: string) => `/organization/${orgGuid}/atlas/analytics/stories/sensitiveContent/mostUsedRequiresReviewContentTags`,
        },
        countByDay: (orgGuid: string) => `/organization/${orgGuid}/atlas/analytics/stories/countByDay`,
      }
    },
    versionList: () => `/atlas/versionList`,
    reviewerList: (orgGuid: string) => `/organization/${orgGuid}/atlas/reviewerList`,
    eventDiscovery: {
      runs: (orgGuid: string) => `/organization/${orgGuid}/atlas/event-discovery/runs`,
      runItems: (orgGuid: string, runGuid: string) => `/organization/${orgGuid}/atlas/event-discovery/runs/${runGuid}/items`,
      categories: (orgGuid: string) => `/organization/${orgGuid}/atlas/event-discovery/categories`,
      cities: (orgGuid: string) => `/organization/${orgGuid}/atlas/event-discovery/cities`,
      schedule: (orgGuid: string) => `/organization/${orgGuid}/atlas/event-discovery/schedule`,
    },
  },
  contentTag: {
    listByStory: (storyGuid: string) => `/story/${storyGuid}/contentTags`,
    list: () => `/contentTags/list`,
  },
  placeCreation: {
    batches: (orgGuid: string) => `/organization/${orgGuid}/place-creation/batches`,
    batchCandidates: (orgGuid: string, batchGuid: string) =>
      `/organization/${orgGuid}/place-creation/batches/${batchGuid}/candidates`,
  },
  rootPlaces: {
    list: (params?: string) => `/root-places${params ? `?${params}` : ''}`,
  },
};

export const createEndpoints = {
  contentFlag: {
    story: (guid: string) => `/story/${guid}/contentFlags`,
    event: (guid: string) => `/event/${guid}/contentFlags`,
    image: (guid: string) => `/image/${guid}/contentFlags`,
    citation: (guid: string) => `/citation/${guid}/contentFlags`,
  } as Record<ContentFlagContentType, (guid: string) => string>,
  place: {
    place: 'place',
    image: (guid: string) => `place/${guid}/image`,
    imageSearch: (guid: string) => `/place/${guid}/image/search`,
    location: (placeGuid: string) => `/place/${placeGuid}/location`,
    timeline: (placeGuid: string) => `/place/${placeGuid}/timeline/append`,
    citation: (placeGuid: string) => `/place/${placeGuid}/citation`,
    adoptPlaces: (placeGuid: string) => `/place/${placeGuid}/adoptPlaces`,
    wikipedia: () => `/wikipedia/places`,
  },
  cleanup: {
    run: (placeGuid: string, cleanupEndpoint: string) =>
      `/cleanup/${placeGuid}/${cleanupEndpoint}`,
  },
  story: {
    story: 'story',
    citation: (storyGuid: string) => `/story/${storyGuid}/citation`,
    image: (storyGuid: string) => `/story/${storyGuid}/image`,
    atlasFailures: (storyGuid: string) => `/story/${storyGuid}/atlasFailures`,
  },
  event: {
    event: 'event',
    image: (guid: string) => `event/${guid}/image`,
    imageSearch: (guid: string) => `/event/${guid}/image/search`,
  },
  guide: {
    guide: 'guide',
    guideStageStory: (guideStageGuid: string, storyGuid: string) =>
      `/guide/stage/${guideStageGuid}/story/${storyGuid}`,
    guideStage: (guideGuid: string) => `/guide/${guideGuid}/stage`,
  },
  image: {
    image: 'image',
    citation: (imageGuid: string) => `/image/${imageGuid}/citation`,
    spotlight: (imageGuid: string) => `image/${imageGuid}/spotlight`,
  },
  organization: {
    image: (orgGuid: string) => `organization/${orgGuid}/image`,
  },
  atlas: {
    generateAtlasStories: (placeGuid: string) => `/atlas/place/${placeGuid}/stories`,
    triggerEventDiscovery: (orgGuid: string) => `/organization/${orgGuid}/atlas/event-discovery/trigger`,
    createEventCategory: (orgGuid: string) => `/organization/${orgGuid}/atlas/event-discovery/categories`,
  },
  contentTag: {
    addContentTagsToStory: (storyGuid: string) => `/story/${storyGuid}/contentTags`,
  },
  storyGroup: {
    ungroup: (storyGroupGuid: string) => `/story-groups/${storyGroupGuid}/ungroup`,
  },
};

export const updateEndpoints = {
  atlas: {
    storyMetadata: (storyGuid: string) => `/story/${storyGuid}/atlasMetadata`,
    eventCategory: (orgGuid: string, categoryGuid: string) =>
      `/organization/${orgGuid}/atlas/event-discovery/categories/${categoryGuid}`,
  },
  user: {
    user: (guid: string) => `user/${guid}`,
  },
  place: {
    properties: (guid: string) => `place/${guid}`,
    imageReorder: (guid: string) => `place/${guid}/images/indices`,
    location: (guid: string) => `place/${guid}/location`,
    batch: () => `/places`,
    merge: (guid: string) => `/place/${guid}/merge`,
  },
  story: {
    story: (guid: string) => `story/${guid}`,
  },
  event: {
    event: (guid: string) => `event/${guid}`,
  },
  organization: {
    organization: (guid: string) => `organization/${guid}`,
  },
  timeline: {
    timeline: (guid: string) => `/timelinestep/${guid}`,
    reindex: '/timelinestep/reindex',
  },
  citation: {
    citation: (guid: string) => `/citation/${guid}`,
  },
  guide: {
    guide: (guideGuid: string) => `/guide/${guideGuid}`,
    stageStoriesReorder: (guideStageGuid: string) =>
      `/guide/stage/${guideStageGuid}/stories/reindex`,
    stagesReorder: (guideGuid: string) => `/guide/${guideGuid}/stages/reindex`,
  },
  storyGroup: {
    reindex: (storyGroupGuid: string) => `/story-groups/${storyGroupGuid}/stories/reindex`,
  },
  image: {
    image: (imageGuid: string) => `image/${imageGuid}`,
  },
  spotlight: {
    spotlight: (spotlightGuid: string) => `spotlight/${spotlightGuid}`,
  },
  contentFlag: {
    byGuid: (guid: string) => `/contentFlag/${guid}`,
  },
  rootPlaces: {
    marketable: (placeGuid: string) => `/root-places/${placeGuid}/marketable`,
  },
};

export const putEndpoints = {
  atlas: {
    eventDiscoverySchedule: (orgGuid: string) =>
      `/organization/${orgGuid}/atlas/event-discovery/schedule`,
  },
  audio: {
    subtitles: (guid: string) => `/audio/${guid}/subtitles`,
  },
  video: {
    subtitles: (guid: string) => `/video/${guid}/subtitles`,
  },
};

export const deleteEndpoints = {
  atlas: {
    eventCategory: (orgGuid: string, categoryGuid: string) =>
      `/organization/${orgGuid}/atlas/event-discovery/categories/${categoryGuid}`,
  },
  contentTag: {
    byGuid: (guid: string) => `/contentTag/${guid}`,
  },
  contentFlag: {
    byGuid: (guid: string) => `/contentFlag/${guid}`,
  },
  place: {
    place: (guid: string) => `place/${guid}`,
    batch: () => `/places`,
  },
  location: {
    place: (locationGuid: string) => `location/${locationGuid}/place`,
  },
  timeline: {
    timeline: (guid: string) => `/timelinestep/${guid}`,
  },
  citation: {
    citation: (guid: string) => `/citation/${guid}`,
  },
  story: {
    story: (guid: string) => `story/${guid}`,
    batch: () => `/stories`,
  },
  event: {
    event: (guid: string) => `event/${guid}`,
  },
  image: {
    image: (guid: string) => `image/${guid}`,
  },
  guide: {
    guide: (guideGuid: string) => `/guide/${guideGuid}`,
    story: (guideStoryGuid: string) => `/guide/stage/story/${guideStoryGuid}`,
    stage: (guideStageGuid: string) => `/guide/stage/${guideStageGuid}`,
  },
  storyGroup: {
    group: (storyGroupGuid: string) => `/story-groups/${storyGroupGuid}`,
  },
  spotlight: {
    spotlight: (spotlightGuid: string) => `spotlight/${spotlightGuid}`,
  },
  audio: {
    subtitles: (guid: string) => `/audio/${guid}/subtitles`,
  },
  video: {
    subtitles: (guid: string) => `/video/${guid}/subtitles`,
  },
};

//---------------------------------------------------------------------------
// GET CALLS
//
//
//---------------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]): Promise<any> => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.get(url, { ...config });

    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

//---------------------------------------------------------------------------
// POST CALLS
//
//
//---------------------------------------------------------------------------

export interface CreationResponse {
  success: boolean;
  message?: string;
  data?: any;
}

// poster can accept either JSON type formats or FormData. It will handle either case apporpriately
export const poster = async (
  url: string,
  data: FormData | Record<string, any>, // Accept either FormData or Record<string, any>
  config?: AxiosRequestConfig
) => {
  try {
    let headers = config?.headers || {};

    // If the data is a Record (plain object), automatically handle it as JSON.
    if (!(data instanceof FormData)) {
      // Add or merge Content-Type for JSON
      headers = {
        ...headers,
        'Content-Type': 'application/json',
      };
    } else {
      headers = {
        ...headers,
        'Content-Type': 'multipart/form-data',
      };
    }

    // Make the POST request with the appropriate data type
    const res = await axiosInstance.post(
      url,
      data instanceof FormData ? data : JSON.stringify(data), // Stringify the data if it's not FormData
      {
        ...config,
        headers,
      }
    );

    return res.data;
  } catch (error) {
    const errorMessage: string = error.response?.data?.error || error.response?.data?.message || error.error || error.message || 'Failed to POST';
    displayAxiosError(errorMessage);
    throw error;
  }
};

//---------------------------------------------------------------------------
// PATCH CALLS
//
//
//---------------------------------------------------------------------------

export interface UpdateResponse {
  success: boolean;
  message?: string;
  data?: any;
}

// poster can accept either JSON type formats or FormData. It will handle either case apporpriately
export const updater = async (
  url: string,
  data: FormData | Record<string, any>, // Accept either FormData or Record<string, any>
  config?: AxiosRequestConfig
) => {
  try {
    let headers = config?.headers || {};

    // If the data is a Record (plain object), automatically handle it as JSON.
    if (!(data instanceof FormData)) {
      // Add or merge Content-Type for JSON
      headers = {
        ...headers,
        'Content-Type': 'application/json',
      };
    }

    // Make the POST request with the appropriate data type
    const res = await axiosInstance.patch(
      url,
      data instanceof FormData ? data : JSON.stringify(data), // Stringify the data if it's not FormData
      {
        ...config,
        headers,
      }
    );

    return res.data;
  } catch (error) {
    const errorMessage: string = error.response?.data?.error || error.response?.data?.message || error.error || error.message || 'Failed to PATCH';
    displayAxiosError(errorMessage);
    throw error;
  }
};

//---------------------------------------------------------------------------
// PUT CALLS
//
//
//---------------------------------------------------------------------------

export interface PutResponse {
  success: boolean;
  message?: string;
  data?: any;
}

// putter can accept either JSON type formats or FormData. It will handle either case appropriately
export const putter = async (
  url: string,
  data: FormData | Record<string, any>, // Accept either FormData or Record<string, any>
  config?: AxiosRequestConfig
) => {
  try {
    let headers = config?.headers || {};

    // If the data is a Record (plain object), automatically handle it as JSON.
    if (!(data instanceof FormData)) {
      // Add or merge Content-Type for JSON
      headers = {
        ...headers,
        'Content-Type': 'application/json',
      };
    }

    // Make the PUT request with the appropriate data type
    const res = await axiosInstance.put(
      url,
      data instanceof FormData ? data : JSON.stringify(data), // Stringify the data if it's not FormData
      {
        ...config,
        headers,
      }
    );

    return res.data;
  } catch (error) {
    const errorMessage: string = error.response?.data?.error || error.response?.data?.message || error.error || error.message || 'Failed to PUT';
    displayAxiosError(errorMessage);
    throw error;
  }
};

//---------------------------------------------------------------------------
// PATCH CALLS
//
//
//---------------------------------------------------------------------------

export interface DeleteResponse {
  success: boolean;
  message?: string;
  data?: any;
}

// poster can accept either JSON type formats or FormData. It will handle either case apporpriately
export const deleter = async (url: string, config?: AxiosRequestConfig) => {
  try {
    const headers = config?.headers || {};

    // Make the POST request with the appropriate data type
    const res = await axiosInstance.delete(url, {
      ...config,
      headers,
    });

    return res.data;
  } catch (error) {
    const errorMessage: string = error.response?.data?.error || error.response?.data?.message || error.error || error.message || 'Failed to DELETE';
    displayAxiosError(errorMessage);
    throw error;
  }
};
